import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    InputAdornment,
    Radio,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Search } from '@mui/icons-material';
import to from 'await-to-js';
import {
    Alert,
    Autocomplete,
    AutoGrid,
    Button,
    DialogV2,
    SelectField,
    TextField,
} from 'c-components';
import DateRangeField from 'c-components/Forms/DateRangeField';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import { generateSearchColumnsData } from 'c-pagination';
import type { InsertionOrder, InsertionOrderKey } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import type { DspTargeting } from './useDspTargeting';
import {
    catalogueOverlapFilters,
    defaultInsertionOrderDateRange,
    diffPicks,
    insertionOrderKey,
    pickRulesByKey,
} from './lib';
import { selectionChanges, selectionRowState } from './selectionChanges';

type Props = {
    campaignId: number;
    open: boolean;
    onClose: () => void;
    dateStart?: string | null;
    dateEnd?: string | null;
    targeting: DspTargeting;
    /** Normal mode: called after Apply saved the ticks (with any skipped-rule message). */
    onApplied?: (message: string | null) => void;
    /**
     * UL-8136 -- `'link'` picks ONE insertion order for a planned line instead of ticking rules.
     * The dialog then writes nothing itself: choosing a row calls `onPick`, and the page links it.
     */
    mode?: 'select' | 'link';
    /** Link mode: resolves null when linked (the page closes the dialog), or the reason it wasn't. */
    onPick?: (io: InsertionOrderKey) => Promise<string | null>;
};

type SearchField = 'insertion_order_name' | 'advertiser' | 'insertion_order_id';

const SEARCH_FIELDS: { label: string; value: SearchField }[] = [
    { label: 'Name', value: 'insertion_order_name' },
    { label: 'Advertiser', value: 'advertiser' },
    { label: 'Insertion order ID', value: 'insertion_order_id' },
];

const keyOf = (io: InsertionOrderKey) => insertionOrderKey(io);

/**
 * UL-7912 -- Insertion Order Selection: the Media Selection modal with insertion orders in it.
 *
 * Catalogue only: an insertion order UniLIVE has no playout data for cannot be attached, so a name
 * is never blank. Each tick saves an `insertion_order_id` / exact rule with no date bound, exactly as
 * a manual media pick does, so a pick and a standing rule detach the same way.
 *
 * Three states per row: ticked (a pick exists), indeterminate (attached only by a broader table
 * parameter, which this modal does not own and will not delete), and unticked.
 *
 * UL-8136 -- in link mode each row has a radio instead: one insertion order is chosen for a planned
 * line, `onPick` is called with it, and no rule is created or deleted here (the link endpoint
 * attaches the IO itself). A refusal is shown in the dialog and nothing changes.
 */
const InsertionOrderSelectionDialog: React.FC<Props> = ({
    campaignId,
    open,
    onClose,
    dateStart,
    dateEnd,
    targeting,
    onApplied,
    mode = 'select',
    onPick,
}) => {
    const t = useCommonTranslation();
    const isLink = mode === 'link';
    const [picked, setPicked] = useState<string | null>(null);
    const [linking, setLinking] = useState(false);
    const [linkError, setLinkError] = useState<string | null>(null);
    const defaultRange = useMemo(
        () => defaultInsertionOrderDateRange(dateStart, dateEnd),
        [dateStart, dateEnd],
    );

    const [customers, setCustomers] = useState<string[]>([]);
    const [range, setRange] = useState<[string, string] | null>(defaultRange);
    const [searchField, setSearchField] = useState<SearchField>('insertion_order_name');
    const [term, setTerm] = useState('');
    const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [rows, setRows] = useState<InsertionOrder[]>([]);
    const [total, setTotal] = useState(0);
    const [customerOptions, setCustomerOptions] = useState<string[]>([]);

    /** Rows the user has ticked, keyed. Seeded from existing picks each time the modal opens. */
    const [ticked, setTicked] = useState<Set<string>>(new Set());
    /** Every row seen while open, so Apply can turn keys back into insertion orders. */
    const [seen, setSeen] = useState<Map<string, InsertionOrderKey>>(new Map());

    const picks = useMemo(() => pickRulesByKey(targeting.rules), [targeting.rules]);

    const catalogue = useAPIClientRequest(apiClient.Entities.Campaign.insertionOrderCatalogue);
    const create = useAPIClientRequest(apiClient.Entities.Campaign.createInsertionOrderRules);
    const remove = useAPIClientRequest(apiClient.Entities.Campaign.deleteInsertionOrderRules);

    useEffect(() => {
        if (!open) return;
        setRange(defaultRange);
        setTicked(new Set(picks.keys()));
        setSeen(new Map());
        setPicked(null);
        setLinkError(null);
        // Only on open: re-seeding while open would throw away the user's ticks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { start: fetchCatalogue } = catalogue;

    const load = useCallback(async () => {
        const filters: Record<string, string[]> = {
            ...catalogueOverlapFilters(range?.[0], range?.[1]),
        };
        if (customers.length > 0) filters['filter.customer'] = customers;

        const [err, res] = await to(
            fetchCatalogue({
                filters,
                page: pagination.page + 1,
                perPage: pagination.pageSize,
                orderBy: 'insertion_order_name',
                direction: 'asc',
                showFilters: true,
                searchables: term.trim()
                    ? generateSearchColumnsData([searchField], term.trim(), false)
                    : {},
            }),
        );

        if (err || !res) return;

        const data = res.data.data ?? [];
        setRows(data);
        setTotal(res.data.meta?.pagination?.total ?? data.length);
        setSeen(current => {
            const next = new Map(current);
            data.forEach(io =>
                next.set(keyOf(io), {
                    customer: io.customer,
                    insertion_order_id: io.insertion_order_id,
                }),
            );
            return next;
        });

        // The customer list is the catalogue's own, so it can only offer DSPs that exist.
        const customerFilter = res.data.meta?.filters?.find(f => f.key === 'filter.customer');
        if (customerFilter?.options?.length) {
            setCustomerOptions(customerFilter.options.map(o => String(o.value)));
        }
    }, [customers, fetchCatalogue, pagination, range, searchField, term]);

    // Debounced, as the Media Selection modal is: typing should not fire a request per keystroke.
    useEffect(() => {
        if (!open) return undefined;
        const handle = window.setTimeout(load, 400);
        return () => window.clearTimeout(handle);
    }, [open, load]);

    const toggle = useCallback((io: InsertionOrder) => {
        const key = keyOf(io);
        setTicked(current => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const pick = useCallback(
        async (io: InsertionOrder) => {
            if (!onPick || linking) return;
            setPicked(keyOf(io));
            setLinking(true);
            setLinkError(null);
            const refused = await onPick({
                customer: io.customer,
                insertion_order_id: io.insertion_order_id,
            });
            setLinking(false);
            if (refused !== null) {
                setPicked(null);
                setLinkError(
                    refused ||
                        t(
                            'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.alreadyHasTarget',
                        ),
                );
            }
        },
        [linking, onPick, t],
    );

    const linkColumn = useMemo<GridColDef<InsertionOrder>>(
        () => ({
            field: 'pick',
            headerName: '',
            width: 56,
            sortable: false,
            renderCell: ({ row }) => (
                <Radio
                    size="small"
                    checked={picked === keyOf(row)}
                    disabled={linking}
                    onChange={() => pick(row)}
                    inputProps={{
                        'aria-label': `${t(
                            'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.link',
                        )}: ${row.insertion_order_name ?? row.insertion_order_id}`,
                    }}
                />
            ),
        }),
        [linking, pick, picked, t],
    );

    const columns = useMemo<GridColDef<InsertionOrder>[]>(
        () => [
            {
                field: 'tick',
                headerName: '',
                width: 56,
                sortable: false,
                renderCell: ({ row }) => {
                    const state = selectionRowState(keyOf(row), ticked, picks, targeting.byKey);
                    const isTicked = state === 'ticked';
                    // Attached, but by a table parameter rather than a pick: shown, not owned.
                    const byRuleOnly = state === 'attachedByRule';

                    return (
                        <Tooltip
                            title={
                                byRuleOnly
                                    ? t(
                                          'Modules.Main.Campaigns.CampaignSchedule.dsp.selection.attachedByRule',
                                      )
                                    : ''
                            }
                        >
                            <Checkbox
                                size="small"
                                checked={isTicked}
                                indeterminate={byRuleOnly}
                                onChange={() => toggle(row)}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                field: 'insertion_order_id',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.columns.insertionOrderId',
                ),
                flex: 1,
                minWidth: 190,
            },
            {
                field: 'insertion_order_name',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.name'),
                flex: 2,
                minWidth: 220,
            },
            {
                field: 'customer',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.customer'),
                width: 110,
            },
            {
                field: 'advertiser',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.advertiser'),
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'min_date',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.minDate'),
                width: 110,
                valueFormatter: ({ value }) => (value ? String(value).slice(0, 10) : '—'),
            },
            {
                field: 'max_date',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.maxDate'),
                width: 110,
                valueFormatter: ({ value }) => (value ? String(value).slice(0, 10) : '—'),
            },
        ],
        [picks, t, targeting.byKey, ticked, toggle],
    );

    const shownColumns = useMemo(
        () => (isLink ? [linkColumn, ...columns.slice(1)] : columns),
        [columns, isLink, linkColumn],
    );

    const { start: startCreate } = create;
    const { start: startRemove } = remove;

    const apply = useCallback(async () => {
        const { newlyTicked, newlyUnticked } = selectionChanges(ticked, picks, seen);

        const { toCreate, toDeleteRuleIds } = diffPicks(
            newlyTicked,
            newlyUnticked,
            targeting.rules,
        );

        let skippedMessage: string | null = null;

        if (toCreate.length > 0) {
            const [err, res] = await to(startCreate(campaignId, toCreate));
            if (err) return;
            const skipped = res?.data?.data?.skipped ?? [];
            if (skipped.length > 0) {
                skippedMessage = t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.selection.skipped',
                    {
                        count: skipped.length,
                    },
                );
            }
        }

        if (toDeleteRuleIds.length > 0) {
            const [err] = await to(startRemove(campaignId, toDeleteRuleIds));
            if (err) return;
        }

        await targeting.refresh();
        onApplied?.(skippedMessage);
        onClose();
    }, [
        campaignId,
        onApplied,
        onClose,
        picks,
        seen,
        startCreate,
        startRemove,
        t,
        targeting,
        ticked,
    ]);

    const busy = create.isLoading || remove.isLoading;

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            maxWidth="xl"
            title={
                isLink
                    ? 'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.link'
                    : 'Modules.Main.Campaigns.CampaignSchedule.dsp.selection.title'
            }
        >
            {catalogue.error && <Alert severity="error">{String(catalogue.error)}</Alert>}
            {linkError && <Alert severity="error">{linkError}</Alert>}

            <AutoGrid xs={12} sm={6} md={3} gap={2} pt={1}>
                <Autocomplete
                    multiple
                    options={customerOptions.map(c => ({ label: c, value: c }))}
                    value={customers}
                    onChange={value => {
                        setCustomers(((value as string[]) ?? []).map(String));
                        setPagination(p => ({ ...p, page: 0 }));
                    }}
                    textFieldProps={{
                        label: t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.customer'),
                        size: 'small',
                    }}
                />
                <DateRangeField
                    value={range ?? [null, null]}
                    onChange={value => {
                        setRange(
                            value?.[0] && value?.[1] ? [String(value[0]), String(value[1])] : null,
                        );
                        setPagination(p => ({ ...p, page: 0 }));
                    }}
                    label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.dateRange')}
                    inputProps={{ size: 'small' }}
                />
                <SelectField
                    label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.searchOn')}
                    options={SEARCH_FIELDS}
                    value={searchField}
                    size="small"
                    onChange={e => setSearchField(e.target.value as SearchField)}
                />
                <TextField
                    label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.search')}
                    value={term}
                    size="small"
                    onChange={e => {
                        setTerm(e.target.value);
                        setPagination(p => ({ ...p, page: 0 }));
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
            </AutoGrid>

            <Typography variant="caption" color="text.secondary">
                {t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.dateRangeHelper')}{' '}
                {t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.catalogueOnly')}
            </Typography>

            <Box minHeight="20rem">
                <DataGridPro
                    rows={rows}
                    columns={shownColumns}
                    getRowId={row => keyOf(row)}
                    rowCount={total}
                    pagination
                    paginationMode="server"
                    paginationModel={pagination}
                    onPaginationModelChange={setPagination}
                    pageSizeOptions={[25, 50, 100]}
                    loading={catalogue.isLoading}
                    disableRowSelectionOnClick
                    density="compact"
                />
            </Box>

            {!isLink && (
                <Stack direction="row" justifyContent="flex-end" gap={2} alignItems="center">
                    {create.error && <Alert severity="error">{String(create.error)}</Alert>}
                    {remove.error && <Alert severity="error">{String(remove.error)}</Alert>}
                    <Button onClick={apply} disabled={busy}>
                        {busy ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('Modules.Main.Campaigns.CampaignSchedule.dsp.selection.apply')
                        )}
                    </Button>
                </Stack>
            )}
        </DialogV2>
    );
};

export default InsertionOrderSelectionDialog;
